import type {CombatTurnToken as Combatant} from "@/api/TurnApi.ts";
import {isAllowedToAct} from "@/encounter/turns/TurnHooks.ts";

export function initiativeOrder(combatants: Combatant[], activeId?: string, nextRoundCombatants?: Combatant[], remainingCombatants?: Combatant[], participantCount?: number) {
    const activeIndex = combatants.findIndex(combatant => combatant.id === activeId);
    const start = Math.max(0, activeIndex);
    const limit = participantCount ?? new Set([...combatants, ...(nextRoundCombatants ?? [])].map(actor => actor.id)).size;
    // Fill the remaining slots with the nearest next-round turns, keeping the bar bounded.
    return {activeIndex, ordered: [...(remainingCombatants ?? combatants.slice(start)),
        ...(nextRoundCombatants ?? combatants.slice(0, start).filter(isAllowedToAct))].slice(0, Math.max(0, limit))};
}

/** Leave room for a second name line below the frame without moving the first line up over the portrait. */
export function initiativeArtworkHeight(size: number, stacked: boolean) {
    return Math.max(size * 1.16, stacked ? 0 : size * 1.005 + 21);
}

/** Reserve the round display first, then keep the nearest turns fully visible and stack the tail. */
export function initiativeLayout(width: number, count: number, roundBreakIndex?: number) {
    const innerWidth = Math.max(0, width - 22);
    const roundSize = Math.min(104, innerWidth * 0.30);
    const gap = Math.min(12, innerWidth * 0.04);
    const available = Math.max(0, innerWidth - roundSize - gap);
    const columnSize = Math.min(100, available / (count > 1 ? 2.5 : 1.2));
    const normalSize = columnSize * 0.86;
    const activeSize = normalSize * 1.15;
    // Reserve only a few pixels around the artwork instead of 14% empty column space.
    const inset = Math.min(4, columnSize * 0.04);
    const normalWidth = normalSize + inset;
    const activeWidth = activeSize + inset;
    const spacing = Math.min(2, normalWidth * 0.03);
    const stackStep = Math.min(12, normalWidth * 0.18);
    const roundGap = Math.min(10, normalWidth * 0.15);
    const beforeEntry = (index: number) => spacing + (index === roundBreakIndex ? roundGap : 0);
    const entries: {index: number; left: number; width: number; size: number; stacked: boolean}[] = [];
    if (count === 0 || available === 0) return {roundSize, gap, entries, hiddenCount: count, trackWidth: 0, roundBreakLeft: undefined};

    const fullWidth = (full: number) => activeWidth + (full - 1) * (normalWidth + spacing)
        + (roundBreakIndex !== undefined && roundBreakIndex > 0 && roundBreakIndex < full ? roundGap : 0);

    let fullCount = 1;
    for (let full = 1; full <= count; full++) {
        const tailWidth = full < count ? beforeEntry(full) + normalWidth : 0;
        if (fullWidth(full) + tailWidth <= available + 0.001) fullCount = full;
        else break;
    }
    for (let index = 0; index < fullCount; index++) {
        entries.push({index, left: index === 0 ? 0 : fullWidth(index) + beforeEntry(index),
            width: index === 0 ? activeWidth : normalWidth, size: index === 0 ? activeSize : normalSize, stacked: false});
    }
    let trackWidth = fullWidth(fullCount);
    if (fullCount < count) {
        let left = trackWidth + beforeEntry(fullCount);
        for (let offset = 0; offset < Math.min(4, count - fullCount); offset++) {
            const index = fullCount + offset;
            if (offset > 0) left += index === roundBreakIndex ? normalWidth + beforeEntry(index) : stackStep;
            if (left + normalWidth > available + 0.001) break;
            entries.push({index, left, width: normalWidth, size: normalSize, stacked: true});
            trackWidth = left + normalWidth;
        }
    }
    const roundBreak = entries.find(entry => entry.index > 0 && entry.index === roundBreakIndex);
    const roundBreakLeft = roundBreak ? roundBreak.left - beforeEntry(roundBreak.index) / 2 : undefined;
    return {roundSize, gap, entries, hiddenCount: count - entries.length, trackWidth, roundBreakLeft};
}
